import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Alert, Spinner, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt, faHome, faChartLine, faCalculator, 
  faTimes, faSync, faArrowRight, faBolt, faBuilding,
  faBed, faBath, faRuler, faCalendar
} from '@fortawesome/free-solid-svg-icons';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

// Set Mapbox access token
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || 'your-mapbox-token-here';

interface PropertyData {
  address: string;
  bedrooms: number;
  bathrooms: number;
  living_area: number;
  year_built: number;
  city: string;
  postal_code: string;
  latitude?: number;
  longitude?: number;
  list_price?: number;
  close_price?: number;
  mls_number?: string;
  distance_miles?: number;
  similarity_score?: number;
  mls_status?: string;
  listing_office_name?: string;
  listing_agent_full_name?: string;
}

interface ComparableProperty extends PropertyData {
  // ARMLS format fields
  UnparsedAddress?: string;
  BedroomsTotal?: number;
  BathsTotal?: number;
  LivingArea?: number;
  YearBuilt?: number;
  ListPrice?: number;
  ClosePrice?: number;
  MlsNumber?: string;
  Latitude?: number;
  Longitude?: number;
  MlsStatus?: string;
  CloseDate?: string;
  ListingOfficeName?: string;
  ListingAgentFullName?: string;
  
  // ✅ MISSING FIELDS FROM PYTHON SCRIPT OUTPUT
  appraiser_score?: number;     // Exists in Python data
  final_score?: number;         // Exists in Python data  
  similarity_score?: number;    // Alternative scoring field
  distance_miles?: number;      // Distance from subject property
  close_date?: string;          // Alternative date field
  search_radius?: number;       // Search radius used
  match_type?: string;          // Match type (exact, etc)
  subdivision?: string;         // Subdivision name
  subdivision_tier?: number;    // Subdivision scoring tier
  subdivision_bonus?: number;   // Subdivision bonus points
}

interface MarketStatistics {
  averagePrice: number;
  medianPrice: number;
  pricePerSqFt: number;
  averageDaysOnMarket: number;
  totalComparables: number;
  priceRange: { min: number; max: number };
  marketTrend: 'up' | 'down' | 'stable';
  trendPercentage: number;
}

interface Step2Props {
  onComplete: (analysisData: any) => void;
  onBack: () => void;
}

const Step2ProfessionalCMA: React.FC<Step2Props> = ({ onComplete, onBack }) => {
  console.log('🎯 [STEP2 PROFESSIONAL CMA] Component loaded!');
  
  // Component is loading correctly
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subjectProperty, setSubjectProperty] = useState<PropertyData | null>(null);
  const [comparables, setComparables] = useState<ComparableProperty[]>([]);
  const [selectedComparables, setSelectedComparables] = useState<Set<string>>(new Set());
  const [marketStats, setMarketStats] = useState<MarketStatistics | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'closed'>('all');
  const [sortBy, setSortBy] = useState<'distance' | 'price' | 'similarity'>('similarity');
  
  // Mapbox refs
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    loadCMAData();
  }, []);

  const loadCMAData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🎯 [PROFESSIONAL CMA] Loading data from sessionStorage...');
      
      // Try multiple sessionStorage keys
      const sessionKeys = ['smartCMAWorkflowData', 'fullCMAResponse'];
      let data = null;
      
      for (const key of sessionKeys) {
        const stored = sessionStorage.getItem(key);
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            
            // Handle different data structures
            if (key === 'smartCMAWorkflowData' && parsed.apiResponse) {
              // Data from Step1 - extract from apiResponse
              data = parsed.apiResponse;
              console.log(`✅ [PROFESSIONAL CMA] Found workflow data in "${key}"`);
              break;
            } else if (parsed.success && (parsed.subject_property || parsed.comparable_properties)) {
              // Direct API response format
              data = parsed;
              console.log(`✅ [PROFESSIONAL CMA] Found API data in "${key}"`);
              break;
            }
          } catch (e) {
            console.log(`❌ [PROFESSIONAL CMA] Failed to parse "${key}"`);
          }
        }
      }
      
      if (!data) {
        throw new Error('No CMA data found. Please complete Step 1 first.');
      }
      
      // Extract subject property
      const subject = data.subject_property;
      if (!subject) {
        throw new Error('Subject property data not found.');
      }
      
      const normalizedSubject: PropertyData = {
        address: subject.address || subject.UnparsedAddress || 'Unknown Address',
        bedrooms: subject.bedrooms || subject.BedroomsTotal || 0,
        bathrooms: subject.bathrooms || subject.BathsTotal || 0,
        living_area: subject.living_area || subject.LivingArea || 0,
        year_built: subject.year_built || subject.YearBuilt || 0,
        city: subject.city || subject.City || '',
        postal_code: subject.postal_code || subject.PostalCode || '',
        latitude: subject.latitude || subject.Latitude,
        longitude: subject.longitude || subject.Longitude,
        list_price: subject.list_price || subject.ListPrice,
        mls_number: subject.mls_number || subject.MlsNumber,
        mls_status: subject.mls_status || subject.MlsStatus,
        listing_office_name: subject.listing_office_name || subject.ListingOfficeName,
        listing_agent_full_name: subject.listing_agent_full_name || subject.ListingAgentFullName
      };
      
      setSubjectProperty(normalizedSubject);
      
      // Extract comparable properties
      let comparableData = [];
      if (data.comparable_properties?.comparables) {
        comparableData = data.comparable_properties.comparables;
      } else if (data.comparable_properties && Array.isArray(data.comparable_properties)) {
        comparableData = data.comparable_properties;
      } else if (data.comparables && Array.isArray(data.comparables)) {
        comparableData = data.comparables;
      }
      
      console.log(`📊 [PROFESSIONAL CMA] Found ${comparableData.length} comparables`);
      
      if (comparableData.length === 0) {
        setError('No comparable properties found for analysis.');
        return;
      }
      
      setComparables(comparableData);
      
      // Select all comparables by default
      const allMlsNumbers = new Set<string>(
        comparableData
          .map((comp: any) => String(comp.MlsNumber || comp.mls_number || ''))
          .filter(Boolean)
      );
      setSelectedComparables(allMlsNumbers);
      
      // Calculate market statistics
      calculateMarketStatistics(comparableData);
      
      // Initialize map
      if (normalizedSubject.latitude && normalizedSubject.longitude) {
        initializeMap(normalizedSubject, comparableData);
      }
      
    } catch (err: any) {
      console.error('❌ [PROFESSIONAL CMA] Error loading data:', err);
      setError(err.message || 'Failed to load CMA data');
    } finally {
      setLoading(false);
    }
  };

  const calculateMarketStatistics = (comparableData: ComparableProperty[]) => {
    if (comparableData.length === 0) return;
    
    // Filter out invalid prices and get valid comparables
    const validComparables = comparableData.filter(comp => {
      const price = comp.ClosePrice || comp.ListPrice || comp.close_price || comp.list_price;
      const sqft = comp.LivingArea || comp.living_area;
      return price && price > 0 && sqft && sqft > 0;
    });
    
    if (validComparables.length === 0) return;
    
    // Calculate prices
    const prices = validComparables.map(comp => 
      comp.ClosePrice || comp.ListPrice || comp.close_price || comp.list_price || 0
    );
    
    const averagePrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
    const sortedPrices = [...prices].sort((a, b) => a - b);
    const medianPrice = sortedPrices[Math.floor(sortedPrices.length / 2)];
    
    // Calculate price per sqft
    const pricePerSqFtValues = validComparables.map(comp => {
      const price = comp.ClosePrice || comp.ListPrice || comp.close_price || comp.list_price || 0;
      const sqft = comp.LivingArea || comp.living_area || 1;
      return price / sqft;
    });
    const avgPricePerSqFt = pricePerSqFtValues.reduce((sum, val) => sum + val, 0) / pricePerSqFtValues.length;
    
    // Simple trend calculation (could be enhanced with time series analysis)
    const recentSales = validComparables.filter(comp => {
      const closeDate = comp.CloseDate || comp.close_date;
      if (!closeDate) return false;
      const saleDate = new Date(closeDate);
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      return saleDate > sixMonthsAgo;
    });
    
    let marketTrend: 'up' | 'down' | 'stable' = 'stable';
    let trendPercentage = 0;
    
    if (recentSales.length >= 2) {
      const recentAvg = recentSales.reduce((sum, comp) => 
        sum + (comp.ClosePrice || comp.ListPrice || 0), 0
      ) / recentSales.length;
      
      const difference = ((recentAvg - averagePrice) / averagePrice) * 100;
      trendPercentage = Math.abs(difference);
      
      if (difference > 2) marketTrend = 'up';
      else if (difference < -2) marketTrend = 'down';
    }
    
    const stats: MarketStatistics = {
      averagePrice: Math.round(averagePrice),
      medianPrice: Math.round(medianPrice),
      pricePerSqFt: Math.round(avgPricePerSqFt),
      averageDaysOnMarket: 30, // Could be calculated from actual DOM data
      totalComparables: validComparables.length,
      priceRange: {
        min: Math.min(...prices),
        max: Math.max(...prices)
      },
      marketTrend,
      trendPercentage: Math.round(trendPercentage)
    };
    
    setMarketStats(stats);
    console.log('📊 [MARKET STATS] Calculated:', stats);
  };

  const initializeMap = (subject: PropertyData, comparableData: ComparableProperty[]) => {
    if (!mapContainer.current || !subject.latitude || !subject.longitude) return;
    
    try {
      // Initialize map centered on subject property
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [subject.longitude, subject.latitude],
        zoom: 14
      });
      
      map.current.on('load', () => {
        if (!map.current) return;
        
        // Add subject property marker (blue)
        const subjectMarker = new mapboxgl.Marker({
          color: '#007bff',
          scale: 1.2
        })
        .setLngLat([subject.longitude!, subject.latitude!])
        .setPopup(new mapboxgl.Popup().setHTML(`
          <div class="p-2">
            <h6 class="mb-1 text-primary">Subject Property</h6>
            <p class="mb-1 small"><strong>${subject.address}</strong></p>
            <p class="mb-1 small">${subject.bedrooms} bed • ${subject.bathrooms} bath • ${subject.living_area?.toLocaleString()} sqft</p>
            <p class="mb-0 small text-success"><strong>$${subject.list_price?.toLocaleString()}</strong></p>
          </div>
        `))
        .addTo(map.current);
        
        // Add comparable property markers (green)
        comparableData.forEach((comp, index) => {
          const lat = comp.Latitude || comp.latitude;
          const lng = comp.Longitude || comp.longitude;
          
          if (lat && lng) {
            const price = comp.ClosePrice || comp.ListPrice || comp.close_price || comp.list_price;
            const address = comp.UnparsedAddress || comp.address || 'Unknown Address';
            const bedrooms = comp.BedroomsTotal || comp.bedrooms || 0;
            const bathrooms = comp.BathsTotal || comp.bathrooms || 0;
            const sqft = comp.LivingArea || comp.living_area || 0;
            const mlsNumber = comp.MlsNumber || comp.mls_number || '';
            const status = comp.MlsStatus || comp.mls_status || '';
            
            const marker = new mapboxgl.Marker({
              color: '#28a745',
              scale: 0.8
            })
            .setLngLat([lng, lat])
            .setPopup(new mapboxgl.Popup().setHTML(`
              <div class="p-2">
                <h6 class="mb-1 text-success">Comparable #${index + 1}</h6>
                <p class="mb-1 small"><strong>${address}</strong></p>
                <p class="mb-1 small">${bedrooms} bed • ${bathrooms} bath • ${sqft?.toLocaleString()} sqft</p>
                <p class="mb-1 small"><strong>$${price?.toLocaleString()}</strong> • ${status}</p>
                <p class="mb-0 small text-muted">MLS# ${mlsNumber}</p>
              </div>
            `))
            .addTo(map.current!);
          }
        });
        
        // Fit map to show all properties
        const coordinates: [number, number][] = [
          [subject.longitude!, subject.latitude!]
        ];
        
        comparableData.forEach(comp => {
          const lat = comp.Latitude || comp.latitude;
          const lng = comp.Longitude || comp.longitude;
          if (lat && lng) {
            coordinates.push([lng, lat]);
          }
        });
        
        if (coordinates.length > 1) {
          const bounds = coordinates.reduce((bounds, coord) => {
            return bounds.extend(coord);
          }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));
          
          map.current.fitBounds(bounds, { padding: 50 });
        }
      });
      
    } catch (error) {
      console.error('❌ [MAPBOX] Failed to initialize map:', error);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up': return '📈';
      case 'down': return '📉';
      default: return '➖';
    }
  };

  const getTrendColor = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up': return 'success';
      case 'down': return 'danger';
      default: return 'secondary';
    }
  };

  const filteredComparables = comparables.filter(comp => {
    if (filterStatus === 'all') return true;
    const status = comp.MlsStatus || comp.mls_status || '';
    if (filterStatus === 'active') return status.toLowerCase().includes('active') || status.toLowerCase().includes('pending');
    if (filterStatus === 'closed') return status.toLowerCase().includes('closed');
    return true;
  });

  const sortedComparables = [...filteredComparables].sort((a, b) => {
    switch (sortBy) {
      case 'distance':
        return (a.distance_miles || 0) - (b.distance_miles || 0);
      case 'price':
        const priceA = a.ClosePrice || a.ListPrice || a.close_price || a.list_price || 0;
        const priceB = b.ClosePrice || b.ListPrice || b.close_price || b.list_price || 0;
        return priceB - priceA;
      case 'similarity':
      default:
        const scoreA = a.similarity_score || a.final_score || a.appraiser_score || 0;
        const scoreB = b.similarity_score || b.final_score || b.appraiser_score || 0;
        return scoreB - scoreA;
    }
  });

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <div 
            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
            style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 0 30px rgba(0, 212, 255, 0.4)',
              animation: 'pulse 2s infinite'
            }}
          >
            <FontAwesomeIcon 
              icon={faBolt} 
              className="text-white" 
              size="2x"
            />
          </div>
          <h4 className="text-dark fw-bold mb-2">Loading Professional CMA Analysis</h4>
          <p className="text-muted">Preparing market intelligence and property mapping...</p>
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="text-center">
          <FontAwesomeIcon icon={faTimes} className="me-2" />
          {error}
          <div className="mt-3">
            <Button variant="outline-danger" onClick={loadCMAData} className="me-2">
              <FontAwesomeIcon icon={faSync} className="me-1" />
              Retry
            </Button>
            <Button variant="outline-secondary" onClick={onBack}>
              ← Back to Step 1
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <div style={{ background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)', minHeight: '100vh' }}>
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div 
              className="p-4 rounded-4"
              style={{
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                border: '1px solid rgba(0, 212, 255, 0.2)'
              }}
            >
              <div className="d-flex align-items-center mb-3">
                <div 
                  className="d-flex align-items-center justify-content-center rounded-circle me-3"
                  style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                    boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)'
                  }}
                >
                  <FontAwesomeIcon icon={faChartLine} className="text-white" size="lg" />
                </div>
                <div>
                  <h2 className="mb-1 text-dark fw-bold">🔥 NEW PROFESSIONAL CMA ANALYSIS 🔥</h2>
                  <p className="mb-0 text-muted">✅ This is the NEW Step2ProfessionalCMA component!</p>
                </div>
              </div>
              
              {subjectProperty && (
                <div className="d-flex align-items-center gap-4">
                  <Badge bg="primary" className="px-3 py-2">
                    <FontAwesomeIcon icon={faHome} className="me-1" />
                    {subjectProperty.address}
                  </Badge>
                  <Badge bg="success" className="px-3 py-2">
                    <FontAwesomeIcon icon={faBuilding} className="me-1" />
                    {comparables.length} Comparables
                  </Badge>
                  {marketStats && (
                    <Badge bg={getTrendColor(marketStats.marketTrend)} className="px-3 py-2">
                      {getTrendIcon(marketStats.marketTrend)} Market {marketStats.marketTrend.toUpperCase()}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* Main Content */}
        <Row>
          {/* Map Column */}
          <Col lg={8}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0 d-flex align-items-center">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-primary me-2" />
                  Property Locations
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                <div 
                  ref={mapContainer} 
                  style={{ 
                    width: '100%', 
                    height: '500px',
                    borderRadius: '0 0 8px 8px'
                  }}
                />
              </Card.Body>
            </Card>
          </Col>

          {/* Statistics Column */}
          <Col lg={4}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0 d-flex align-items-center">
                  <FontAwesomeIcon icon={faCalculator} className="text-success me-2" />
                  Market Statistics
                </h5>
              </Card.Header>
              <Card.Body>
                {marketStats ? (
                  <div className="space-y-3">
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="text-muted">Average Price</span>
                      <strong className="text-success">{formatPrice(marketStats.averagePrice)}</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="text-muted">Median Price</span>
                      <strong>{formatPrice(marketStats.medianPrice)}</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="text-muted">Price per Sq Ft</span>
                      <strong>${marketStats.pricePerSqFt}</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="text-muted">Price Range</span>
                      <small>{formatPrice(marketStats.priceRange.min)} - {formatPrice(marketStats.priceRange.max)}</small>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="text-muted">Market Trend</span>
                      <Badge bg={getTrendColor(marketStats.marketTrend)}>
                        {getTrendIcon(marketStats.marketTrend)} {marketStats.trendPercentage}%
                      </Badge>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2">
                      <span className="text-muted">Total Comparables</span>
                      <strong className="text-primary">{marketStats.totalComparables}</strong>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-muted">
                    <Spinner size="sm" className="me-2" />
                    Calculating statistics...
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Subject Property Card */}
            {subjectProperty && (
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-transparent border-0">
                  <h5 className="mb-0 d-flex align-items-center">
                    <FontAwesomeIcon icon={faHome} className="text-primary me-2" />
                    Subject Property
                  </h5>
                </Card.Header>
                <Card.Body>
                  <h6 className="text-primary mb-2">{subjectProperty.address}</h6>
                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <div className="text-center p-2 bg-light rounded">
                        <FontAwesomeIcon icon={faBed} className="text-primary mb-1" />
                        <div className="fw-bold">{subjectProperty.bedrooms}</div>
                        <small className="text-muted">Beds</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center p-2 bg-light rounded">
                        <FontAwesomeIcon icon={faBath} className="text-info mb-1" />
                        <div className="fw-bold">{subjectProperty.bathrooms}</div>
                        <small className="text-muted">Baths</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center p-2 bg-light rounded">
                        <FontAwesomeIcon icon={faRuler} className="text-warning mb-1" />
                        <div className="fw-bold">{subjectProperty.living_area?.toLocaleString()}</div>
                        <small className="text-muted">Sq Ft</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center p-2 bg-light rounded">
                        <FontAwesomeIcon icon={faCalendar} className="text-secondary mb-1" />
                        <div className="fw-bold">{subjectProperty.year_built}</div>
                        <small className="text-muted">Built</small>
                      </div>
                    </div>
                  </div>
                  {subjectProperty.list_price && (
                    <div className="text-center p-3 bg-primary bg-opacity-10 rounded">
                      <h4 className="text-primary mb-0">{formatPrice(subjectProperty.list_price)}</h4>
                      <small className="text-muted">Current List Price</small>
                    </div>
                  )}
                  {subjectProperty.listing_office_name && (
                    <p className="mt-2 mb-0 text-muted small" style={{ fontStyle: 'italic' }}>
                      Courtesy of {subjectProperty.listing_office_name}
                      {subjectProperty.listing_agent_full_name && ` - ${subjectProperty.listing_agent_full_name}`}
                    </p>
                  )}
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>

        {/* Comparables Table */}
        <Row className="mt-4">
          <Col>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 d-flex align-items-center">
                    <FontAwesomeIcon icon={faBuilding} className="text-success me-2" />
                    Comparable Properties ({sortedComparables.length})
                  </h5>
                  <div className="d-flex gap-2">
                    <Form.Select 
                      size="sm" 
                      value={filterStatus} 
                      onChange={(e) => setFilterStatus(e.target.value as any)}
                      style={{ width: 'auto' }}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active/Pending</option>
                      <option value="closed">Closed</option>
                    </Form.Select>
                    <Form.Select 
                      size="sm" 
                      value={sortBy} 
                      onChange={(e) => setSortBy(e.target.value as any)}
                      style={{ width: 'auto' }}
                    >
                      <option value="similarity">Sort by Match</option>
                      <option value="distance">Sort by Distance</option>
                      <option value="price">Sort by Price</option>
                    </Form.Select>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <div style={{ overflowX: 'auto', maxHeight: '400px' }}>
                  <Table className="mb-0" hover>
                    <thead className="bg-light sticky-top">
                      <tr>
                        <th className="border-0 py-3">Property</th>
                        <th className="border-0 py-3">Price</th>
                        <th className="border-0 py-3">Bed/Bath</th>
                        <th className="border-0 py-3">Sq Ft</th>
                        <th className="border-0 py-3">$/SqFt</th>
                        <th className="border-0 py-3">Distance</th>
                        <th className="border-0 py-3">Match %</th>
                        <th className="border-0 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedComparables.map((comp, index) => {
                        const price = comp.ClosePrice || comp.ListPrice || comp.close_price || comp.list_price || 0;
                        const sqft = comp.LivingArea || comp.living_area || 1;
                        const pricePerSqft = Math.round(price / sqft);
                        const address = comp.UnparsedAddress || comp.address || 'Unknown Address';
                        const bedrooms = comp.BedroomsTotal || comp.bedrooms || 0;
                        const bathrooms = comp.BathsTotal || comp.bathrooms || 0;
                        const mlsNumber = comp.MlsNumber || comp.mls_number || '';
                        const status = comp.MlsStatus || comp.mls_status || '';
                        const distance = comp.distance_miles || 0;
                        const similarity = comp.similarity_score || comp.final_score || comp.appraiser_score || 0;
                        
                        return (
                          <tr key={mlsNumber || index}>
                            <td className="py-3">
                              <div>
                                <strong className="text-dark">{address}</strong>
                                <br />
                                <small className="text-muted">MLS# {mlsNumber}</small>
                              </div>
                            </td>
                            <td className="py-3">
                              <strong className="text-success">{formatPrice(price)}</strong>
                            </td>
                            <td className="py-3 text-center">
                              {bedrooms}/{bathrooms}
                            </td>
                            <td className="py-3 text-center">
                              {sqft?.toLocaleString()}
                            </td>
                            <td className="py-3 text-center">
                              <strong>${pricePerSqft}</strong>
                            </td>
                            <td className="py-3 text-center">
                              {distance.toFixed(2)} mi
                            </td>
                            <td className="py-3 text-center">
                              <Badge bg="primary" className="px-2 py-1">
                                {Math.round(similarity)}%
                              </Badge>
                            </td>
                            <td className="py-3">
                              <Badge 
                                bg={status.toLowerCase().includes('closed') ? 'success' : 
                                    status.toLowerCase().includes('pending') ? 'warning' : 'info'}
                                className="px-2 py-1"
                              >
                                {status}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Action Buttons */}
        <Row className="mt-4">
          <Col className="text-center">
            <div className="d-flex gap-3 justify-content-center">
              <Button 
                variant="outline-secondary" 
                size="lg"
                onClick={onBack}
                className="px-4"
              >
                ← Back to Step 1
              </Button>
              
              <Button 
                variant="primary"
                size="lg"
                onClick={() => {
                  // Create complete analysis data for Step 3 with real data only
                  const analysisData = {
                    success: true,
                    subject_property: subjectProperty,
                    comparable_properties: {
                      comparables: sortedComparables
                    },
                    marketStats,
                    selectedComparables: Array.from(selectedComparables),
                    // Real comparable statistics from our analysis
                    comparable_stats: {
                      count: sortedComparables.length,
                      price_min: marketStats?.priceRange.min,
                      price_max: marketStats?.priceRange.max,
                      price_avg: marketStats?.averagePrice
                    }
                  };
                  
                  // Save to sessionStorage for Step 3 to read
                  sessionStorage.setItem('fullCMAResponse', JSON.stringify(analysisData));
                  
                  // Call the onComplete callback
                  onComplete(analysisData);
                }}
                className="px-5"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)'
                }}
              >
                <FontAwesomeIcon icon={faArrowRight} className="me-2" />
                Continue to AI Price Prediction
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Step2ProfessionalCMA;